import React, { useMemo } from 'react';

import { faEye, faFolder, faLayerGroup } from '@fortawesome/pro-regular-svg-icons';
// Example icon for stack
import styled from 'styled-components';

import { FileModel, TagModel } from 'models';

import { Icon } from 'core/ui';

// Use shared types
import { PreparedFile, SortCriteria } from './PatientFiles.types';
import { FileThumbnailDisplay } from './PatientFilesHelperComponents';

// -----------------------------------
// Constants
// -----------------------------------

// Helper function to sort files consistently based on criteria
const sortFilesByCriteria = (files: PreparedFile[], criteria: SortCriteria): PreparedFile[] => {
  const sorted = [...files];
  switch (criteria) {
    case 'Oldest':
      sorted.sort((a, b) => {
        const dateA = a.sortableDate?.getTime() ?? 0;
        const dateB = b.sortableDate?.getTime() ?? 0;
        return dateA - dateB;
      });
      break;
    case 'Name':
      sorted.sort((a, b) => (a.fileName ?? '').localeCompare(b.fileName ?? ''));
      break;
    case 'Newest':
    default:
      sorted.sort((a, b) => {
        const dateA = a.sortableDate?.getTime() ?? 0;
        const dateB = b.sortableDate?.getTime() ?? 0;
        return dateB - dateA;
      });
      break;
  }
  return sorted;
};

// -----------------------------------
// Styled components
// -----------------------------------
const StackedGrid = styled.div<{ numCols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ numCols }) => numCols}, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Single column on mobile */
  }
`;

const StackedItemCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: pointer; // Make the whole card clickable

  &:hover {
    z-index: 20;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StackedThumbnail = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1; /* Square thumbnail */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .placeholder-icon {
    font-size: 40px;
    color: #ccc;
    padding: 1rem;
  }
`;

const StackCountBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 10;
  pointer-events: none; // Let clicks pass through to card
`;

const StackedItemInfo = styled.div`
  padding: 8px 12px;
  background-color: #ffffff;
  border-top: 1px solid #eee;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackDate = styled.div`
  color: #6c757d;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
`;

const StackFileName = styled.div`
  font-weight: 500;
  color: #343a40;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  font-size: 0.9rem;
`;

const NoResultsMessage = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 40px;
  color: #6c757d;
  font-size: 1rem;
  grid-column: 1 / -1; // Span all columns if grid is present
`;

const TagBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  color: ${({ theme }) => theme.colors.palette.grayscale[0]};
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.75rem;
  margin-right: 4px;
  margin-bottom: 4px;
  display: inline-block;
  white-space: nowrap;
`;

const TagContainer = styled.div`
  margin-bottom: 4px;
  line-height: 1.4;
  max-height: 4.2em; // Limit height (e.g., 3 lines)
  overflow: hidden;
`;

const WorklistLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(99, 102, 241, 0.95) 100%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 768px) {
    top: 6px;
    left: 6px;
    padding: 3px 6px;
    font-size: 0.7rem;
  }
`;

const ViewIconButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  color: #333;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  pointer-events: all; // Allow clicks on this button

  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
    top: 6px;
    right: 6px;
  }
`;

const EmptyFolderPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #6c757d;
`;

// -----------------------------------
// Stacked Item Component
// -----------------------------------
interface StackedItemProps {
  files: PreparedFile[]; // Array of files in the stack, sorted newest first
  groupKey: string; // The key for this group
  groupDisplayName?: string; // Display name for this group (e.g., "CT", "Chest X-Ray")
  onStackSelect: (files: PreparedFile[], groupKey?: string) => void;
  tagIdToTagMap?: Map<number, TagModel>;
  onFileSelect?: (file: FileModel) => void; // Handler for preview selection
  onFileView?: (file: FileModel) => void; // Handler for opening full view of individual files
  isWorklistGroup?: boolean; // Whether this group represents a worklist folder
}

const StackedItem: React.FC<StackedItemProps> = React.memo(
  ({ files, groupKey, groupDisplayName, onStackSelect, tagIdToTagMap, onFileSelect, onFileView, isWorklistGroup = false }) => {
    // Handle empty worklist folders
    if (!files || files.length === 0) {
      if (isWorklistGroup && groupDisplayName) {
        // Show empty folder
        return (
          <StackedItemCard onClick={() => onStackSelect([], groupKey)}>
            <StackedThumbnail>
              <WorklistLabel>{groupDisplayName}</WorklistLabel>
              <StackCountBadge>
                <Icon icon={faFolder} size="sm" />0
              </StackCountBadge>
              <EmptyFolderPlaceholder>
                <Icon icon={faFolder} size="2x" style={{ opacity: 0.3 }} />
                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '8px' }}>Empty</div>
              </EmptyFolderPlaceholder>
            </StackedThumbnail>
            <StackedItemInfo>
              <StackFileName title={groupDisplayName}>{groupDisplayName}</StackFileName>
              <StackDate>No files</StackDate>
            </StackedItemInfo>
          </StackedItemCard>
        );
      }
      return null; // Non-worklist empty groups are not rendered
    }

    const representativeFile = files[0]; // Most recent file
    const stackCount = files.length;
    const isIndividualFile = stackCount === 1; // Single file, not a stack

    const handleClick = () => {
      if (isIndividualFile && onFileSelect) {
        onFileSelect(representativeFile);
      } else {
        onStackSelect(files, groupKey);
      }
    };

    const handleViewClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent the card click
      if (onFileView) {
        onFileView(representativeFile);
      }
    };

    // Get tags from the representative file (since all files in stack have the same tags)
    const getTagNames = (inputFile: PreparedFile): string[] => {
      const currentTagMap = tagIdToTagMap ?? new Map<number, TagModel>();
      const names = new Set<string>();
      inputFile.tagIds?.forEach((id) => {
        const tag = currentTagMap.get(id);
        if (tag) names.add(tag.name);
      });
      inputFile.derivedTagNames?.forEach((name) => names.add(name));
      return Array.from(names);
    };
    const tagNames = getTagNames(representativeFile);

    return (
      <StackedItemCard onClick={handleClick}>
        <StackedThumbnail>
          {groupDisplayName && <WorklistLabel>{groupDisplayName}</WorklistLabel>}
          {!isIndividualFile && (
            <StackCountBadge>
              <Icon icon={isWorklistGroup ? faFolder : faLayerGroup} size="sm" />
              {stackCount}
            </StackCountBadge>
          )}
          {isIndividualFile && (
            <ViewIconButton onClick={handleViewClick} title="View File">
              <Icon icon={faEye} />
            </ViewIconButton>
          )}
          <FileThumbnailDisplay file={representativeFile} viewType="gallery" />
        </StackedThumbnail>
        <StackedItemInfo>
          <StackFileName title={representativeFile.fileName}>{representativeFile.fileName}</StackFileName>
          {tagNames.length > 0 && (
            <TagContainer title={tagNames.join(', ')}>
              {tagNames.map((name) => (
                <TagBadge key={name}>{name}</TagBadge>
              ))}
            </TagContainer>
          )}
          <StackDate title={isIndividualFile ? representativeFile.displayDateString : `Newest: ${representativeFile.displayDateString}`}>
            {isIndividualFile ? representativeFile.displayDateString : `Newest: ${representativeFile.displayDateString}`}
          </StackDate>
          {/* Optionally show date range if more than one file */}
          {!isIndividualFile && files[stackCount - 1] && (
            <StackDate title={`Oldest: ${files[stackCount - 1].displayDateString}`}>Oldest: {files[stackCount - 1].displayDateString}</StackDate>
          )}
        </StackedItemInfo>
      </StackedItemCard>
    );
  },
);
StackedItem.displayName = 'StackedItem';

// -----------------------------------
// Props Interface for Stacked View
// -----------------------------------
interface GroupMetadata {
  key: string;
  displayName: string;
  worklistId?: number;
  worklistName?: string;
}

interface PatientFilesStackedViewProps {
  groupedFiles: Map<string, PreparedFile[]>; // Map of groupKey -> files
  groupMetadata?: Map<string, GroupMetadata>; // Map of groupKey -> metadata
  isLoading: boolean;
  numberOfColumns: number;
  onStackSelect: (files: PreparedFile[], groupKey?: string) => void; // Handler when a stack is clicked
  tagIdToTagMap?: Map<number, TagModel>;
  onFileSelect?: (file: FileModel) => void; // Handler for preview selection
  onFileView?: (file: FileModel) => void; // Handler for opening full view of individual files
  sortCriteria: SortCriteria; // Sort criteria to apply consistently
}

// -----------------------------------
// Component
// -----------------------------------
export const PatientFilesStackedView: React.FC<PatientFilesStackedViewProps> = ({
  groupedFiles,
  groupMetadata,
  isLoading,
  numberOfColumns,
  onStackSelect,
  tagIdToTagMap,
  onFileSelect,
  onFileView,
  sortCriteria,
}) => {
  // Convert map entries to an array for rendering - moved up to fix hook order
  const groupEntries = Array.from(groupedFiles.entries());

  // Determine if we should show individual files instead of stacks
  // This happens when:
  // 1. There's only one group and it's the default "all-files" group
  // 2. Or when the group is "unassigned-files" (files that don't belong to meaningful categories)
  // 3. Or when there's only 1 stack and no unstacked files (show all files as unstacked)
  // 4. Or when groups don't represent meaningful categorization (no worklist-based grouping)
  // 5. But NOT when we have multiple worklist groups (All Files view should show worklist folders)
  const shouldShowIndividualFiles = useMemo(() => {
    if (groupEntries.length === 1) {
      const [groupKey] = groupEntries[0];
      // If it's the default "all-files" or "unassigned-files" group, show individual files
      if (groupKey === 'all-files' || groupKey === 'unassigned-files') {
        return true;
      }
      // If there's only 1 stack and no unstacked files, show all files as unstacked
      return true;
    }

    // If we have multiple groups and they represent worklists, always show as stacks
    const hasWorklistGroups = groupEntries.some(([groupKey]) => {
      const metadata = groupMetadata?.get(groupKey);
      return metadata?.worklistId !== undefined;
    });

    if (hasWorklistGroups) {
      return false; // Show as stacked worklist folders
    }

    return false;
  }, [groupEntries, groupMetadata]);

  // Create a unified list of items to render (both stacks and individual files)
  const unifiedItems = useMemo(() => {
    const items: {
      type: 'stack' | 'individual';
      files: PreparedFile[];
      groupKey: string;
      groupDisplayName?: string;
      isWorklistGroup?: boolean;
      sortDate: Date; // Representative date for sorting
    }[] = [];

    groupEntries.forEach(([groupKey, filesInGroup]) => {
      const metadata = groupMetadata?.get(groupKey);
      const sortedFiles = sortFilesByCriteria(filesInGroup, sortCriteria);

      // For "unassigned-files" groups, add individual files to the unified list
      if (groupKey === 'unassigned-files') {
        sortedFiles.forEach((file) => {
          items.push({
            type: 'individual',
            files: [file],
            groupKey: `individual-${file.id}`,
            sortDate: file.sortableDate ?? new Date(0),
          });
        });
      } else {
        // For regular groups (including empty worklist folders), add as stacks
        const representativeDate = sortedFiles.length > 0 ? (sortedFiles[0].sortableDate ?? new Date(0)) : new Date(0); // Empty folders get earliest date

        items.push({
          type: 'stack',
          files: sortedFiles,
          groupKey,
          groupDisplayName: metadata?.displayName,
          isWorklistGroup: !!metadata?.worklistId,
          sortDate: representativeDate,
        });
      }
    });

    // Sort the unified items based on the sort criteria
    items.sort((a, b) => {
      switch (sortCriteria) {
        case 'Oldest': {
          return a.sortDate.getTime() - b.sortDate.getTime();
        }
        case 'Name': {
          // For name sorting, use the first file's name or group display name
          const nameA = a.groupDisplayName ?? a.files[0]?.fileName ?? '';
          const nameB = b.groupDisplayName ?? b.files[0]?.fileName ?? '';
          return nameA.localeCompare(nameB);
        }
        case 'Newest':
        default: {
          return b.sortDate.getTime() - a.sortDate.getTime();
        }
      }
    });

    return items;
  }, [groupEntries, groupMetadata, sortCriteria]);

  if (isLoading) {
    // Optional: Show placeholders or a simplified loading state
    return <StackedGrid numCols={numberOfColumns}>{/* Loading skeleton? */}</StackedGrid>;
  }

  if (groupedFiles.size === 0) {
    return <NoResultsMessage>No document groups found matching the current filters.</NoResultsMessage>;
  }

  // If we should show individual files, render them directly without stacking
  if (shouldShowIndividualFiles) {
    const [, filesInGroup] = groupEntries[0];
    // Sort files using the consistent sorting function
    const sortedFiles = sortFilesByCriteria(filesInGroup, sortCriteria);

    return (
      <StackedGrid numCols={numberOfColumns}>
        {sortedFiles.map((file) => (
          <StackedItem
            key={file.id}
            files={[file]} // Single file, no stacking
            groupKey={`individual-${file.id}`}
            groupDisplayName={undefined} // No group display name for individual files
            onStackSelect={onStackSelect}
            tagIdToTagMap={tagIdToTagMap}
            onFileSelect={onFileSelect}
            onFileView={onFileView}
            isWorklistGroup={false}
          />
        ))}
      </StackedGrid>
    );
  }

  // Unified view: render both stacks and individual files in sorted order
  return (
    <StackedGrid numCols={numberOfColumns}>
      {unifiedItems.map((item) => {
        if (item.type === 'individual') {
          // Render individual file
          return (
            <StackedItem
              key={item.groupKey}
              files={item.files}
              groupKey={item.groupKey}
              groupDisplayName={undefined}
              onStackSelect={onStackSelect}
              tagIdToTagMap={tagIdToTagMap}
              onFileSelect={onFileSelect}
              onFileView={onFileView}
              isWorklistGroup={false}
            />
          );
        } else {
          // Render stack (including empty worklist folders)
          if (item.files.length === 0 && item.isWorklistGroup) {
            // Empty worklist folder
            return (
              <StackedItem
                key={item.groupKey}
                files={[]}
                groupKey={item.groupKey}
                groupDisplayName={item.groupDisplayName}
                onStackSelect={onStackSelect}
                tagIdToTagMap={tagIdToTagMap}
                onFileSelect={onFileSelect}
                onFileView={onFileView}
                isWorklistGroup={true}
              />
            );
          } else if (item.files.length > 0) {
            // Regular stack with files
            return (
              <StackedItem
                key={item.groupKey}
                files={item.files}
                groupKey={item.groupKey}
                groupDisplayName={item.groupDisplayName}
                onStackSelect={onStackSelect}
                tagIdToTagMap={tagIdToTagMap}
                onFileSelect={onFileSelect}
                onFileView={onFileView}
                isWorklistGroup={!!item.isWorklistGroup}
              />
            );
          }
          return null; // Skip empty non-worklist groups
        }
      })}
    </StackedGrid>
  );
};
